// src/pages/ActionLogPage.js
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  Paper,
  CircularProgress,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Link as MuiLink,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FilterListIcon from "@mui/icons-material/FilterList";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../components/ConfirmDialog";
import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import CrmDialog from "../components/CrmDialog";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import { navigateToLeadDetail } from "../utils/leadDetailNavigation";


/** Lead PK for /leads/:id — only for action types that should show "View Lead". */
function viewLeadIdForLog(log) {
  if (!log?.object_id) return null;
  const ct = (log.content_type || "").toLowerCase();
  const meta =
    log.metadata && typeof log.metadata === "object" ? log.metadata : {};

  if (log.action_type === "lead_updated") {
    if (ct === "lead" || !log.content_type) return log.object_id;
    return null;
  }
  if (
    log.action_type === "followup_created" ||
    log.action_type === "followup_updated"
  ) {
    if (ct === "lead") return log.object_id;
    if (ct === "followup") {
      const lid = meta.lead_id;
      if (lid == null) return null;
      const n = typeof lid === "number" ? lid : Number(lid);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
  return null;
}

const ACTION_TYPE_COLORS = {
  login: "success",
  logout: "default",
  lead_created: "primary",
  lead_updated: "info",
  lead_deleted: "error",
  lead_viewed: "default",
  followup_created: "success",
  followup_updated: "info",
  followup_completed: "success",
  followup_deleted: "error",
  task_created: "primary",
  task_updated: "info",
  task_completed: "success",
  task_deleted: "error",
  notification_created: "warning",
  notification_deleted: "error",
  route_created: "primary",
  map_opened: "info",
  settings_accessed: "default",
  report_generated: "info",
  admin_action: "error",
  other: "default",
};

export default function ActionLogPage() {
  const navigate = useNavigate();
  const { role, isReadOnly } = useAuth();
  
  // ✅ Get user from localStorage (since useAuth doesn't return user)
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr && userStr !== "undefined" && userStr !== "null") {
        setCurrentUser(JSON.parse(userStr));
      }
    } catch (err) {
      console.error("Failed to parse user from localStorage:", err);
    }
  }, []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
 
 

  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [dateRange, setDateRange] = useState("this_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [cleanupOpen, setCleanupOpen] = useState(false);
const [cleanupDays, setCleanupDays] = useState(30);


  /* ----------------------------- Load Users ----------------------------- */
  const fetchUsers = useCallback(async () => {
    try {
      const res = await API.get("/users/");
      setUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, []);

  /* ----------------------------- Load Logs ------------------------------ */
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      /* ⭐ -----------------------------------------------------------Correct Role-Based Filtering */
      const isAdmin = role === "Admin" || role === "Sales Director";

      if (!isAdmin) {
        // Non-admin → always restrict to themselves
        // ✅ Add null check
        if (currentUser?.id) {
          params.set("user_id", currentUser.id);
        } else {
          // User not loaded yet, skip fetch
          setLoading(false);
          return;
        }
      } else {
        // Admin/Director → Only filter when a specific user is selected
        if (selectedUser && selectedUser !== "") {
          params.set("user_id", selectedUser);
        }
      }

      /* ⭐ Correct Date Filtering */
      if (dateRange) params.set("date_range", dateRange);

      if (dateRange === "custom") {
        if (startDate) params.set("start_date", startDate);
        if (endDate) params.set("end_date", endDate);
      }

      const url = `/action-logs/?${params.toString()}`;
      const res = await API.get(url);

      setLogs(res.data || []);
    } catch (err) {
      console.error("Error fetching action logs:", err);
      toast.error("Failed to load action logs.");
    } finally {
      setLoading(false);
    }
  }, [
    role,
    currentUser?.id, // ✅ Use optional chaining
    selectedUser,
    dateRange,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /* ------------------------- Delete Log Handler ------------------------- */
  const handleDeleteLog = (log) => {
    setLogToDelete(log);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!logToDelete) return;

    try {
      await API.delete(`/action-logs/${logToDelete.id}/`);
      toast.success("Activity log deleted successfully!");
      await fetchLogs();
      setConfirmDelete(false);
      setLogToDelete(null);
    } catch (err) {
      console.error("Error deleting log:", err);
      toast.error("Failed to delete activity log.");
    }
  };
const handleCleanupLogs = async () => {
  if (isReadOnly) return; // Kill the click for read-only users
  
  try {
    const response = await API.post("/action-logs/cleanup/", {
      older_than_days: cleanupDays,
    });

    const deletedCount = response.data?.deleted || 0;
    
    if (deletedCount > 0) {
      toast.success(`✅ Successfully deleted ${deletedCount} activity log(s) older than ${cleanupDays} days.`);
    } else {
      toast.info(`ℹ️ No logs found older than ${cleanupDays} days to delete.`);
    }
    
    setCleanupOpen(false);
    fetchLogs();
  } catch (err) {
    console.error("Error cleaning up activity logs:", err);
    const errorMessage = err.response?.data?.detail || err.message || "Failed to clean up activity logs.";
    toast.error(`❌ ${errorMessage}`);
  }
};

  /* --------------------------- Format Timestamp ------------------------- */
  const formatTimestamp = (timestamp) =>
    new Date(timestamp).toLocaleString();

  /* ---------------------------- Action Icons ---------------------------- */
  const getActionIcon = (actionType) => {
    if (actionType.includes("login")) return "🔐";
    if (actionType.includes("lead")) return "👤";
    if (actionType.includes("followup")) return "📅";
    if (actionType.includes("task")) return "✅";
    if (actionType.includes("route")) return "🗺️";
    if (actionType.includes("map")) return "📍";
    if (actionType.includes("notification")) return "🔔";
    return "📝";
  };

  /* ---------------------------------------------------------------------- */

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <AccessTimeIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Action Log
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View detailed activity logs and user actions
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Card>

        <Stack spacing={2}>
        {/* ------------------------------ Filters ------------------------------ */}
        <Card data-tour="action-log-filters">
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <FilterListIcon color="primary" />
                <Typography variant="h6">Filters</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {/* --------------------------------------------------------User Filter */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>User</InputLabel>
                  <Select
                    value={selectedUser}
                    label="User"
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.first_name} {u.last_name} ({u.username})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* -------------------------------------------------------Date Range */}
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    label="Date Range"
                    onChange={(e) => setDateRange(e.target.value)}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="this_week">This Week</MenuItem>
                    <MenuItem value="this_month">This Month</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>

                {/* ------------------------------------------------------------Custom Range */}
                {dateRange === "custom" && (
                  <>
                    <TextField
                      type="date"
                      label="Start Date"
                      size="small"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      size="small"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                )}

                <Button
                  variant="contained"
                  onClick={fetchLogs}
                  startIcon={<FilterListIcon />}
                >
                  Apply Filters
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* ------------------------------- Logs ------------------------------- */}
        <Card>
          <CardContent>
           <Box
  data-tour="action-log-table-header"
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2,
  }}
>
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <HistoryIcon color="primary" />
    <Typography variant="h6">
      Activity Logs ({logs.length})
    </Typography>
  </Box>

  {(role === "Admin" || role === "Sales Director") && (
    <DisabledButtonWrapper 
      isDisabled={isReadOnly}
      tooltipText="Read-only mode: This action is disabled"
    >
      <Button
        variant="outlined"
        size="small"
        startIcon={<DeleteIcon />}
        onClick={() => {
          if (isReadOnly) return;
          setCleanupOpen(true);
        }}
        disabled={isReadOnly}
        sx={{
          textTransform: "none",
          borderRadius: 2,
          fontWeight: 600,
          color:
            theme.palette.mode === "dark"
              ? "#e5e7eb"
              : "text.primary",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.25)"
              : "rgba(0,0,0,0.25)",
          opacity: isReadOnly ? 0.6 : 1,
          cursor: isReadOnly ? 'not-allowed' : 'pointer',
          visibility: 'visible',
          display: 'inline-flex',
          "&:hover": {
            borderColor:
              theme.palette.mode === "dark"
                ? isReadOnly ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.45)"
                : isReadOnly ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.45)",
            bgcolor:
              theme.palette.mode === "dark"
                ? isReadOnly ? "transparent" : "rgba(255,255,255,0.06)"
                : isReadOnly ? "transparent" : "rgba(0,0,0,0.04)",
          },
        }}
      >
        Clean Up Logs
      </Button>
    </DisabledButtonWrapper>
  )}
</Box>


            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : logs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No activity logs found for the selected filters.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {logs.map((log) => (
                  <Paper
                    key={log.id}
                    sx={{
                      p: 1.5,
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 1.5,
                      borderLeft: `4px solid`,
                      borderColor: `${ACTION_TYPE_COLORS[log.action_type] || "info"}.main`,
                    }}
                  >
                    <Box sx={{ flex: 1, width: "100%" }}>
                      {/* -------------------------------------------------------✅ Mobile: Stack user name and badge vertically */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: { xs: 0.5, sm: 1 },
                          mb: { xs: 0.25, sm: 0.25 },
                        }}
                      >
                        {/* ------------------------------------------------------Line 1: User Name */}
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{ mb: { xs: 0.25, sm: 0 } }}
                        >
                          {log.user_full_name || log.user_username || "Unknown User"}
                        </Typography>

                        {/* -------------------------------------------------------Line 2 on mobile, inline on desktop: Badge + View Lead */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Chip
                            label={log.action_type_display}
                            size="small"
                            color={
                              ACTION_TYPE_COLORS[log.action_type] || "default"
                            }
                          />
                          {(() => {
                            const leadId = viewLeadIdForLog(log);
                            return leadId ? (
                              <MuiLink
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() =>
                                  navigateToLeadDetail(navigate, leadId, {
                                    pathname: "/settings/action-log",
                                    label: "Action Log",
                                  })
                                }
                                sx={{
                                  fontSize: "0.8125rem",
                                  fontWeight: 600,
                                  border: 0,
                                  background: "none",
                                  cursor: "pointer",
                                  p: 0,
                                  fontFamily: "inherit",
                                }}
                              >
                                View Lead
                              </MuiLink>
                            ) : null;
                          })()}
                        </Box>
                      </Box>
                      {/* ------------------------------------------------------------------Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: { xs: 0.5, sm: 0 } }}
                      >
                        {log.description}
                      </Typography>
                    </Box>

                    {/* --------------------------------------------------------------Timestamp and Delete - stack on mobile */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "row", sm: "row" },
                        alignItems: "center",
                        gap: 1,
                        alignSelf: { xs: "flex-start", sm: "center" },
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: { xs: "space-between", sm: "flex-end" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="caption" color="text.secondary">
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Box>

                      {(role === "Admin" || role === "Sales Director") && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteLog(log)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* -----------------------------------------------Delete Confirm Dialog */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => {
          setConfirmDelete(false);
          setLogToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Activity Log"
        message="Are you sure you want to delete this activity log? This action cannot be undone."
        itemName={
          logToDelete
            ? `${logToDelete.action_type_display} - ${logToDelete.description}`
            : null
        }
        severity="error"
      />


<CrmDialog
  open={cleanupOpen}
  onClose={() => setCleanupOpen(false)}
  maxWidth="xs"
  fullWidth
>
  <Typography variant="h6" fontWeight={700} mb={1}>
    Clean Up Activity Log
  </Typography>

  <Typography variant="body2" color="text.secondary" mb={2}>
    This will permanently delete non-critical activity entries
    older than the selected time period.
  </Typography>

  <TextField
    select
    fullWidth
    label="Delete activity older than"
    value={cleanupDays}
    onChange={(e) => setCleanupDays(Number(e.target.value))}
    sx={{ mb: 3 }}
  >
    <MenuItem value={7}>7 days</MenuItem>
    <MenuItem value={30}>30 days</MenuItem>
    <MenuItem value={90}>90 days</MenuItem>
  </TextField>

  <Stack direction="row" spacing={1} justifyContent="flex-end">
    <Button onClick={() => setCleanupOpen(false)}>
      Cancel
    </Button>
    <DisabledButtonWrapper 
      isDisabled={isReadOnly}
      tooltipText="Read-only mode: This action is disabled"
    >
      <Button
        variant="contained"
        color="error"
        sx={{ 
          color: "#fff",
          opacity: isReadOnly ? 0.6 : 1,
          cursor: isReadOnly ? 'not-allowed' : 'pointer',
          visibility: 'visible',
          display: 'inline-flex',
        }}
        onClick={handleCleanupLogs}
        disabled={isReadOnly}
      >
        Delete
      </Button>
    </DisabledButtonWrapper>
  </Stack>
</CrmDialog>
      </Box>
    </Box>
  );
}
